import { Alert, Button } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import type { AnalysisRecord } from '@features/PostCallAnalysis/config/types';
import type { PostCallAnalysisConfig } from '@features/PostCallAnalysis/config/types';
import { evaluateKpi } from '@features/PostCallAnalysis/utils/evaluateKpi';
import { getChartData } from '@features/PostCallAnalysis/utils/chartData';
import { applyMapper } from '@features/PostCallAnalysis/utils/dataMappers';
import { MOCK_ANALYSIS_CALLS } from '@features/PostCallAnalysis/mock/calls.mock';
import { defaultCallAnalysisConfig } from '@features/PostCallAnalysis/config/defaultCallAnalysis.config';
import { useCampaignCallListControllerCallListQuery } from '@shared/services/apiService/apiService';
import CallAnalysisPeriodFilter from './CallAnalysisPeriodFilter';
import {
  DEFAULT_STATISTICS_PERIOD,
  StatisticsPeriod,
  resolveStatisticsPeriodRange,
} from './statisticsPeriod';

interface CallAnalysisStatisticsProps {
  campaign: Record<string, unknown>;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatKpiValue(value: number | string, format?: string): string {
  if (typeof value === 'string') return value;
  switch (format) {
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'decimal':
      return value.toFixed(2);
    case 'duration':
      return formatDuration(value);
    case 'number':
    default:
      return String(Math.round(value));
  }
}

function getData(source: Record<string, unknown>, path: string): AnalysisRecord[] {
  const raw = get(source, path) as unknown[] | undefined;
  return (raw ?? []) as AnalysisRecord[];
}

function renderSection(
  config: PostCallAnalysisConfig,
  dataSource: Record<string, unknown>,
  theme: Theme,
  t: (key: string) => string,
) {
  const kpiValues = config.kpis.map((kpi) => {
    const data = getData(dataSource, kpi.path);
    return { ...kpi, value: evaluateKpi(data, kpi.formula) };
  });

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {kpiValues.map((kpi) => (
          <Paper
            key={kpi.id}
            elevation={0}
            data-testid={kpi.id === 'total_calls' ? 'statistics-kpi-total-calls' : undefined}
            sx={{
              p: 2,
              minWidth: 160,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t(kpi.label)}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={600}
              data-testid={
                kpi.id === 'total_calls' ? 'statistics-kpi-total-calls-value' : undefined
              }>
              {formatKpiValue(kpi.value, kpi.format)}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Grid container spacing={3}>
        {config.charts.map((chartDef) => {
          const rawData = getData(dataSource, chartDef.path);
          const data = applyMapper(rawData, chartDef.mapper);
          const chartData = getChartData(data, chartDef);
          if (chartData.length === 0) return null;

          const title = t(chartDef.title);

          if (chartDef.type === 'pie') {
            return (
              <Grid item xs={12} md={6} key={chartDef.id}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    {title}
                  </Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={({ name, percent }: { name: string; percent: number }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }>
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill ?? theme.palette.primary.main}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => [value, 'count']} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            );
          }

          if (chartDef.type === 'barHorizontal') {
            return (
              <Grid item xs={12} md={6} key={chartDef.id}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    {title}
                  </Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      layout="vertical"
                      data={chartData}
                      margin={{ left: 8, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={210}
                        interval={0}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="value" name={title} radius={[0, 4, 4, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill ?? theme.palette.primary.main}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            );
          }

          return (
            <Grid item xs={12} md={6} key={chartDef.id}>
              <Paper
                elevation={0}
                sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {title}
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" name={title} radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.fill ?? theme.palette.primary.main}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

const CallAnalysisStatistics: React.FC<CallAnalysisStatisticsProps> = ({ campaign }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const campaignId = campaign?.id as string | undefined;
  const [period, setPeriod] = useState<StatisticsPeriod>(DEFAULT_STATISTICS_PERIOD);
  const range = useMemo(() => resolveStatisticsPeriodRange(period), [period]);
  const awaitingCustomRange = period.preset === 'custom' && range == null;
  const {
    data: callsList,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useCampaignCallListControllerCallListQuery(
    { id: campaignId!, start: range?.start, end: range?.end },
    { skip: !campaignId || awaitingCustomRange },
  );
  const previousCallsRef = useRef<typeof callsList>(undefined);
  if (callsList) {
    previousCallsRef.current = callsList;
  }
  const displayedCalls = callsList ?? (isFetching ? previousCallsRef.current : undefined) ?? [];
  const showInitialLoader = (isLoading || isFetching) && !callsList && !previousCallsRef.current;

  const realDataSource = useMemo(
    () => ({ ...campaign, calls: displayedCalls }),
    [campaign, displayedCalls],
  );

  const mockDataSource = useMemo(() => ({ analysis: { calls: MOCK_ANALYSIS_CALLS } }), []);

  if (isError)
    return (
      <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
        Unable to load current analysis data.
      </Alert>
    );
  return (
    <Grid
      container
      sx={{
        margin: '24px',
        flexDirection: 'column',
        gap: 3,
        width: '96%',
      }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        {t('campaignPage.analysisSummary.title')}
      </Typography>

      <CallAnalysisPeriodFilter value={period} onChange={setPeriod} />

      {awaitingCustomRange ? (
        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
          <Typography color="text.secondary">
            {t('campaignPage.analysisSummary.period.selectRange')}
          </Typography>
        </Paper>
      ) : showInitialLoader ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }} aria-busy={isFetching}>
          {isFetching && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: (muiTheme) =>
                  muiTheme.palette.mode === 'dark'
                    ? alpha(muiTheme.palette.common.black, 0.35)
                    : alpha(muiTheme.palette.common.white, 0.55),
              }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {renderSection(defaultCallAnalysisConfig, realDataSource, theme, t)}
        </Box>
      )}

      {/* <Divider sx={{ my: 3 }} /> */}

      {/* {renderSection(smilesPostCallAnalysisConfig, mockDataSource, theme, t)} */}
    </Grid>
  );
};

export default CallAnalysisStatistics;
